<template>
  <Dialog
    :visible="true"
    modal
    :closable="false"
    :close-on-escape="false"
    :draggable="false"
    :style="{ width: 'calc(100vw - 48px)', height: 'calc(100dvh - 48px)', maxWidth: 'none', maxHeight: 'none' }"
    :pt="{ root: { class: 'element-picker overflow-hidden', 'aria-labelledby': titleId } }"
    @update:visible="cancel">
    <template #container>
      <div ref="modal" class="flex h-full min-h-0 flex-col bg-[var(--rm-app-card)]">
        <header class="flex shrink-0 items-center gap-3 border-b border-line bg-panel py-2.5 pr-3 pl-5">
          <div class="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3 gap-y-0.5">
            <h2 :id="titleId" class="text-[15px]/[1.3] font-bold text-heading">{{
              options.placement ? `Choose where ${options.placement.label} appears` : 'Pick elements on your site'
            }}</h2>
            <p class="text-[13px]/[1.3] text-muted" aria-live="polite">{{ helper }}</p>
          </div>
          <Button label="Cancel" size="small" severity="secondary" outlined @click="cancel" />
          <Button size="small" :disabled="picks.length === 0" @click="finish">
            Done<span class="element-picker-done-count">{{ picks.length }}</span>
          </Button>
          <span class="h-5 w-px bg-line" aria-hidden="true" />
          <button
            type="button"
            aria-label="Close"
            title="Close"
            class="flex size-8 shrink-0 items-center justify-center rounded-full text-muted hover:bg-overlay hover:text-heading"
            @click="cancel">
            <XMarkIcon class="size-[18px]" aria-hidden="true" />
          </button>
        </header>

        <div class="flex h-[52px] shrink-0 items-center gap-1 border-b border-line bg-canvas px-3">
          <button
            type="button"
            class="element-picker-nav"
            aria-label="Back"
            title="Back"
            :disabled="history.index <= 0"
            @click="go(-1)">
            <ArrowLeftIcon class="size-[17px]" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="element-picker-nav"
            aria-label="Forward"
            title="Forward"
            :disabled="history.index >= history.entries.length - 1"
            @click="go(1)">
            <ArrowRightIcon class="size-[17px]" aria-hidden="true" />
          </button>
          <button type="button" class="element-picker-nav" aria-label="Reload" title="Reload" @click="reload">
            <ArrowPathIcon class="size-[17px]" aria-hidden="true" />
          </button>
          <form class="element-picker-address" @submit.prevent="navigate">
            <LockClosedIcon class="size-3.5 shrink-0 text-muted" aria-hidden="true" />
            <input
              v-model="address"
              aria-label="Page URL or path"
              spellcheck="false"
              autocomplete="off"
              class="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-ink shadow-none outline-none focus:ring-0" />
            <button
              type="button"
              aria-label="Open in new tab"
              title="Open in new tab"
              class="flex size-7 shrink-0 items-center justify-center rounded-full text-muted hover:bg-overlay hover:text-heading"
              @click="openInNewTab">
              <ArrowTopRightOnSquareIcon class="size-[15px]" aria-hidden="true" />
            </button>
          </form>
          <SelectButton
            v-model="mode"
            :options="MODES"
            option-label="label"
            option-value="value"
            :allow-empty="false"
            aria-label="Picker mode"
            class="element-picker-mode">
            <template #option="{ option }"
              ><component :is="option.icon" class="size-4" aria-hidden="true" />{{ option.label }}</template
            >
          </SelectButton>
        </div>

        <div v-if="options.placement" class="shrink-0 border-b border-line bg-panel px-5 py-3">
          <div class="flex flex-wrap items-center gap-3">
            <span class="text-sm font-semibold text-heading">Place {{ options.placement.label }}</span>
            <SelectButton
              :model-value="position"
              :options="positionOptions"
              option-label="label"
              option-value="value"
              option-disabled="disabled"
              :allow-empty="false"
              aria-label="Placement position"
              @update:model-value="setPosition" />
            <span class="text-xs text-muted">{{
              options.placement.single ? 'Choose one spot on any page.' : 'One spot per page. Browse to add more pages.'
            }}</span>
          </div>
          <details class="mt-2 text-sm" :class="{ invisible: !picks.length }">
            <summary class="cursor-pointer text-copy">Advanced</summary>
            <div class="mt-2 flex max-h-40 flex-col gap-2 overflow-auto">
              <label v-for="pick in picks" :key="pickKey(pick)" class="flex flex-col gap-1">
                <span class="text-xs text-muted">{{ pick.domain }}{{ pick.path }}</span>
                <input
                  :value="pick.selector"
                  :aria-label="`Placement selector for ${pick.path}`"
                  spellcheck="false"
                  class="rounded border border-line bg-canvas px-2 py-1 font-mono text-xs text-ink"
                  @change="editPlacementSelector(pickKey(pick), $event)" />
                <span
                  v-if="onPage?.has(pickKey(pick)) && (details.get(pickKey(pick))?.matchCount ?? 0) > 1"
                  class="text-warn text-xs">
                  This selector matches more than one element on this page. Only the first will be used.
                </span>
                <span v-if="/:nth-(?:child|of-type)\(/.test(pick.selector)" class="text-warn text-xs">
                  This selector may break if your page layout changes.
                </span>
              </label>
            </div>
          </details>
        </div>

        <p
          v-if="error"
          role="alert"
          class="shrink-0 border-b border-line bg-danger-soft px-5 py-2 text-[13px] text-danger">
          {{ error }}
        </p>

        <div class="flex min-h-0 flex-1">
          <div ref="viewport" class="relative min-w-0 flex-1 overflow-hidden bg-canvas">
            <iframe
              v-if="frameUrl && !usingPopup"
              ref="iframe"
              :key="frameKey"
              :src="frameUrl"
              title="Website element picker"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              class="transition-opacity duration-200"
              :style="{
                width: `${FRAME_WIDTH}px`,
                height: `${frameHeight}px`,
                transform: `scale(${scale})`,
                transformOrigin: '0 0',
                border: '0',
                opacity: loading ? 0.55 : 1
              }"
              @error="loadFailed" />
            <div v-if="loading" role="status" class="absolute top-0 left-0 z-10 h-0.5 w-[65%] bg-primary">
              <span class="sr-only">Loading your site…</span>
            </div>
            <div
              v-if="usingPopup"
              class="pointer-events-none absolute inset-0 flex items-center justify-center bg-canvas/90">
              <p role="status" class="max-w-md p-6 text-center text-sm text-copy">{{
                loading
                  ? 'Waiting for the picker in the new tab…'
                  : 'Pick elements in the new tab. Your selections appear here; return to finish.'
              }}</p>
            </div>
            <ElementPickerDock
              v-if="narrow"
              :items="items"
              @hover="focus"
              @remove="remove"
              @locate="locate"
              @clear="clear" />
          </div>
          <ElementPickerPanel
            v-if="!narrow"
            :items="items"
            :alt-key="ALT_KEY"
            @hover="focus"
            @remove="remove"
            @locate="locate"
            @select-parent="selectParent"
            @update-selector="updateSelector"
            @clear="clear" />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, useId, useTemplateRef, watch } from 'vue'
import { useElementSize, watchDebounced } from '@vueuse/core'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import SelectButton from 'primevue/selectbutton'
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  CursorArrowRaysIcon,
  LockClosedIcon,
  ViewfinderCircleIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'
import type { PickElementsOptions, ElementPickerResult } from '@dashboard/composables/useElementPicker'
import {
  acceptsPickerMessage,
  isPickerMessage,
  isPickOnPage,
  isPlacementPick,
  joinPickerSelectors,
  pickKey,
  replacePlacementPick,
  samePick,
  splitSelectorList
} from '@dashboard/services/elementPickerProtocol'
import type {
  PickerPick,
  PickDetail,
  PickerMessage,
  PlacementPosition
} from '@dashboard/services/elementPickerProtocol'
import { createRoverPickSession } from '@dashboard/services/roverUrl'
import type { RoverPickSession } from '@dashboard/services/roverUrl'
import { normalizePagePath } from '@dashboard/modules/widget/services/pageMatching'
import { useConfigStore } from '@dashboard/stores/configStore'
import ElementPickerDock from './ElementPickerDock.vue'
import ElementPickerPanel from './ElementPickerPanel.vue'
import type { ElementPickerItem } from './elementPickerItem'

const FRAME_WIDTH = 1440
/** Below this modal width the side panel would squeeze the page, so selections move to a floating dock. */
const PANEL_MIN_MODAL_WIDTH = 1100
const ALT_KEY = /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌥' : 'Alt'
const MODES = [
  { label: 'Pick', value: 'pick', icon: ViewfinderCircleIcon },
  { label: 'Browse', value: 'browse', icon: CursorArrowRaysIcon }
]

/** Our own back/forward stack: the frame is cross-origin, and remounting it keeps the dashboard's history clean. */
type FrameHistory = { entries: string[]; index: number; pending: number | null }

const props = defineProps<{ options: PickElementsOptions }>()
const emit = defineEmits<{ complete: [result: ElementPickerResult | null] }>()
const configStore = useConfigStore()
const titleId = useId()
const modal = useTemplateRef<HTMLElement>('modal')
const iframe = useTemplateRef<HTMLIFrameElement>('iframe')
const viewport = useTemplateRef<HTMLElement>('viewport')
const { width: modalWidth } = useElementSize(modal)
const narrow = computed(() => modalWidth.value > 0 && modalWidth.value < PANEL_MIN_MODAL_WIDTH)
const currentUrl = ref(props.options.url)
const address = ref(props.options.url)
const currentPath = ref(normalizePagePath(props.options.url) ?? '/')
const currentDomain = computed(() => new URL(currentUrl.value).hostname)
const picks = ref<PickerPick[]>(
  props.options.placement
    ? props.options.initial
      ? [
          {
            selector: props.options.initial,
            path: null,
            domain: null,
            position: props.options.placement.position ?? 'after'
          }
        ]
      : []
    : splitSelectorList(props.options.initial ?? '').map((selector) => ({ selector, path: null, domain: null }))
)
const details = shallowRef(new Map<string, PickDetail>())
const position = ref<PlacementPosition>(props.options.placement?.position ?? 'after')
const currentPick = computed(() =>
  picks.value.find((pick) => isPickOnPage(pick, currentPath.value, currentDomain.value))
)
const candidateAllowsInside = ref(false)
const canInsertInside = computed(
  () =>
    candidateAllowsInside.value &&
    (!currentPick.value || details.value.get(pickKey(currentPick.value))?.canInsertInside !== false)
)
const positionOptions = computed(() => [
  { label: 'Above', value: 'before' },
  { label: 'Below', value: 'after' },
  { label: 'Inside', value: 'inside', disabled: !canInsertInside.value }
])
/** Keys of picks the shown page can find; null until the page reports. */
const onPage = shallowRef<Set<string> | null>(null)
const mode = ref<'pick' | 'browse'>('pick')
/** Shared with the page: whichever side sees Alt change tells the other. */
const altHeld = ref(false)
const loading = ref(true)
const error = ref('')
const frameUrl = ref('')
const frameKey = ref(0)
const usingPopup = ref(false)
const history = ref<FrameHistory>({ entries: [], index: -1, pending: null })
const width = ref(FRAME_WIDTH)
const height = ref(800)
const scale = computed(() => width.value / FRAME_WIDTH)
const frameHeight = computed(() => height.value / scale.value)
let popup: Window | null = null
let timeout: ReturnType<typeof setTimeout> | undefined
let resizeObserver: ResizeObserver | undefined
let session: RoverPickSession | null = null
let activeOrigin = ''
let loadGeneration = 0
let closed = false

const helper = computed(() => {
  if (mode.value === 'browse') {
    return 'Browsing. Clicks go to the page; nothing gets selected.'
  }
  if (altHeld.value) {
    return `Browsing while ${ALT_KEY} is held. Clicks go to the page.`
  }
  if (props.options.placement) {
    return props.options.placement.single
      ? 'Click a section to choose a spot. A new spot replaces the previous one.'
      : 'Click a section to choose a spot. A new spot on this page replaces the previous one.'
  }
  return props.options.restrictTo === 'form'
    ? 'Click a form to select it. Click again to remove it.'
    : 'Click an element to select it. Click again to remove it.'
})

const items = computed<ElementPickerItem[]>(() =>
  picks.value.map((pick, index) => {
    const key = pickKey(pick)
    const detail = details.value.get(key)
    const found = onPage.value?.has(key) ?? false
    let note: string | null = null
    // The page has reported what it can find, so `currentUrl` is the loaded page.
    if (onPage.value && !found) {
      const host = new URL(currentUrl.value).hostname
      note =
        pick.path !== null && (pick.path !== currentPath.value || (pick.domain !== null && pick.domain !== host))
          ? `On ${pick.domain ?? ''}${pick.path}`
          : 'Not found on this page'
    }
    return {
      key,
      number: index + 1,
      label: detail?.label ?? (pick.path === null ? 'Saved element' : 'Element'),
      text: detail?.text ?? '',
      note,
      selector: pick.selector,
      onPage: found,
      canSelectParent: !props.options.placement && found && Boolean(detail?.parentSelector)
    }
  })
)

function loadFailed() {
  loading.value = false
  error.value = 'This page could not load in the picker. Try opening it in a new tab from the address bar.'
}

/** Picks are Vue proxies. `postMessage` structured-clones its payload and rejects them with DataCloneError. */
function snapshotForPostMessage(message: PickerMessage): PickerMessage {
  const snapshot: unknown = JSON.parse(JSON.stringify(message))
  if (!isPickerMessage(snapshot)) {
    throw new Error('Picker message could not be posted.')
  }
  return snapshot
}

function send(message: PickerMessage) {
  const target = usingPopup.value ? popup : iframe.value?.contentWindow
  if (!closed && target && session && activeOrigin) {
    target.postMessage({ ...snapshotForPostMessage(message), nonce: session.nonce }, activeOrigin)
  }
}

function initializePage() {
  send({
    type: 'rm-picker:init',
    restrictTo: props.options.restrictTo,
    multiple: props.options.placement ? true : (props.options.multiple ?? false),
    picks: picks.value.map((pick) => ({ ...pick })),
    browse: mode.value === 'browse',
    scale: usingPopup.value ? 1 : scale.value,
    placement: props.options.placement ? { label: props.options.placement.label, position: position.value } : undefined
  })
}

function finish() {
  closeSession()
  if (props.options.placement) {
    emit('complete', {
      picks: picks.value.filter(isPlacementPick).map(({ selector, path, domain, position }) => ({
        selector,
        path,
        domain,
        position
      }))
    })
  } else {
    emit('complete', {
      selector: joinPickerSelectors(picks.value),
      picks: picks.value.map((pick) => ({ ...pick }))
    })
  }
}

function cancel() {
  closeSession()
  emit('complete', null)
}

function findPick(key: string): { pick: PickerPick; index: number } | null {
  const index = picks.value.findIndex((pick) => pickKey(pick) === key)
  const pick = picks.value[index]
  return pick ? { pick, index } : null
}

function setPicks(next: PickerPick[]) {
  picks.value = next
  initializePage()
}

function setPosition(value: PlacementPosition) {
  position.value = value
  setPicks(
    picks.value.map((pick) =>
      isPlacementPick(pick) && isPickOnPage(pick, currentPath.value, currentDomain.value)
        ? { ...pick, position: value }
        : pick
    )
  )
}

function remove(key: string) {
  setPicks(picks.value.filter((pick) => pickKey(pick) !== key))
}

function clear() {
  setPicks([])
}

function focus(key: string | null) {
  send({ type: 'rm-picker:focus', pick: (key && findPick(key)?.pick) || null, scroll: false })
}

function locate(key: string) {
  const found = findPick(key)
  if (found) {
    send({ type: 'rm-picker:focus', pick: { ...found.pick }, scroll: true })
  }
}

/** Swap a pick for another selector in place, keeping its number unless that selector is already picked. */
function replacePick(key: string, selector: string) {
  const found = findPick(key)
  if (!found) {
    return
  }
  const next = { ...found.pick, selector }
  setPicks(
    picks.value.some((pick) => samePick(pick, next))
      ? picks.value.filter((_, index) => index !== found.index)
      : picks.value.map((pick, index) => (index === found.index ? next : pick))
  )
}

function selectParent(key: string) {
  const parentSelector = details.value.get(key)?.parentSelector
  if (parentSelector) {
    replacePick(key, parentSelector)
  }
}

function updateSelector(key: string, selector: string) {
  replacePick(key, selector)
}

function editPlacementSelector(key: string, event: Event) {
  const input = event.target as HTMLInputElement
  const selector = input.value.trim()
  try {
    if (!selector) {
      throw new Error('empty')
    }
    document.createDocumentFragment().querySelector(selector)
    input.setCustomValidity('')
    error.value = ''
    if (findPick(key)?.pick.selector !== selector) {
      replacePick(key, selector)
    }
  } catch {
    input.setCustomValidity('Enter a valid CSS selector.')
    input.reportValidity()
    error.value = 'Enter a valid CSS selector.'
  }
}

function setAltHeld(held: boolean) {
  if (altHeld.value !== held) {
    altHeld.value = held
    send({ type: 'rm-picker:alt', held })
  }
}

function onKey(event: KeyboardEvent) {
  if (event.key === 'Alt') {
    setAltHeld(event.type === 'keydown')
  } else if (event.key === 'Escape' && event.type === 'keydown') {
    send({ type: 'rm-picker:clear-hover' })
  }
}

function onBlur() {
  setAltHeld(false)
}

function recordVisit(url: string) {
  const { entries, index, pending } = history.value
  if (pending !== null) {
    history.value = {
      entries: entries.map((entry, at) => (at === pending ? url : entry)),
      index: pending,
      pending: null
    }
  } else if (entries[index] !== url) {
    history.value = { entries: [...entries.slice(0, index + 1), url], index: index + 1, pending: null }
  }
}

function receive(event: MessageEvent<unknown>) {
  const source = usingPopup.value ? popup : (iframe.value?.contentWindow ?? null)
  if (
    closed ||
    !session ||
    !acceptsPickerMessage(event, session.origin, source, session.nonce, true) ||
    !isPickerMessage(event.data)
  ) {
    return
  }
  const message = event.data
  activeOrigin = event.origin
  switch (message.type) {
    case 'rm-picker:ready':
      clearTimeout(timeout)
      loading.value = false
      error.value = ''
      onPage.value = null
      altHeld.value = false
      initializePage()
      break
    case 'rm-picker:loading':
      startLoading()
      break
    case 'rm-picker:navigate':
      history.value = { ...history.value, pending: null }
      void load(new URL(message.url))
      break
    case 'rm-picker:navigated':
      currentUrl.value = message.url
      address.value = message.url
      currentPath.value = message.path
      if (props.options.placement && isPlacementPick(currentPick.value)) {
        position.value = currentPick.value.position
      }
      recordVisit(message.url)
      break
    case 'rm-picker:pick': {
      const pick = {
        selector: message.selector,
        path: message.path,
        domain: message.domain
      }
      if (props.options.placement) {
        if (!isPlacementPick(message)) {
          return
        }
        position.value = message.position === 'inside' && message.canInsertInside === false ? 'after' : message.position
        const placement = { ...pick, position: position.value }
        setPicks(props.options.placement.single ? [placement] : replacePlacementPick(picks.value, placement))
      } else if (!props.options.multiple) {
        setPicks([pick])
      } else if (!picks.value.some((existing) => samePick(existing, pick))) {
        setPicks([...picks.value, pick])
      }
      break
    }
    case 'rm-picker:unpick':
      setPicks(picks.value.filter((pick) => !samePick(pick, message)))
      break
    case 'rm-picker:details': {
      const next = new Map(details.value)
      for (const detail of message.picks) {
        next.set(pickKey(detail), detail)
      }
      details.value = next
      onPage.value = new Set(message.picks.map(pickKey))
      break
    }
    case 'rm-picker:ghost':
      candidateAllowsInside.value = message.canInsertInside
      break
    case 'rm-picker:alt':
      altHeld.value = message.held
      break
  }
}

function startLoading() {
  clearTimeout(timeout)
  loading.value = true
  error.value = ''
  timeout = setTimeout(loadFailed, 20_000)
}

async function load(target: URL) {
  const generation = ++loadGeneration
  session = null
  activeOrigin = ''
  frameUrl.value = ''
  currentUrl.value = target.href
  address.value = target.href
  currentPath.value = normalizePagePath(target.href) ?? '/'
  startLoading()
  try {
    const teamPid = configStore.currentTeamPid
    if (!teamPid) {
      throw new Error('Select a project before picking elements.')
    }
    const minted = await createRoverPickSession(teamPid, target.href)
    if (closed || generation !== loadGeneration) {
      return
    }
    session = minted
    activeOrigin = minted.origin
    frameUrl.value = minted.url
    frameKey.value++
    if (usingPopup.value && popup) {
      popup.location.href = minted.url
    }
  } catch {
    if (!closed && generation === loadGeneration) {
      clearTimeout(timeout)
      loadFailed()
    }
  }
}

function navigate() {
  try {
    const target = new URL(address.value.trim(), currentUrl.value)
    if (!['http:', 'https:'].includes(target.protocol)) {
      throw new Error('Enter an http:// or https:// page URL.')
    }
    history.value = { ...history.value, pending: null }
    load(target)
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Enter a valid page URL.'
  }
}

function go(step: -1 | 1) {
  const index = history.value.index + step
  const url = history.value.entries[index]
  if (url !== undefined) {
    history.value = { ...history.value, pending: index }
    load(new URL(url))
  }
}

function reload() {
  history.value = { ...history.value, pending: history.value.index >= 0 ? history.value.index : null }
  load(new URL(currentUrl.value))
}

function openInNewTab() {
  const opened = window.open('about:blank', '_blank')
  if (!opened) {
    error.value = 'Your browser blocked the new tab. Allow popups for this dashboard and try again.'
    return
  }
  popup?.close()
  popup = opened
  usingPopup.value = true
  void load(new URL(currentUrl.value))
}

function closeSession() {
  closed = true
  loadGeneration++
  session = null
  clearTimeout(timeout)
  popup?.close()
}

watch(mode, (value) => send({ type: 'rm-picker:mode', browse: value === 'browse' }))
watch(position, (value) => send({ type: 'rm-picker:position', position: value }))
watchDebounced(scale, initializePage, { debounce: 100 })

watch(viewport, (element) => {
  resizeObserver?.disconnect()
  if (!element) {
    return
  }
  resizeObserver = new ResizeObserver(([entry]) => {
    if (entry && entry.contentRect.width > 0 && entry.contentRect.height > 0) {
      width.value = entry.contentRect.width
      height.value = entry.contentRect.height
    }
  })
  resizeObserver.observe(element)
})

onMounted(() => {
  window.addEventListener('message', receive)
  window.addEventListener('keydown', onKey)
  window.addEventListener('keyup', onKey)
  window.addEventListener('blur', onBlur)
  navigate()
})
onBeforeUnmount(() => {
  closeSession()
  resizeObserver?.disconnect()
  window.removeEventListener('message', receive)
  window.removeEventListener('keydown', onKey)
  window.removeEventListener('keyup', onKey)
  window.removeEventListener('blur', onBlur)
})
</script>

<style scoped>
.element-picker-done-count {
  display: inline-flex;
  min-width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  margin-left: 2px;
  padding: 0 5px;
  border-radius: 9999px;
  background: rgb(255 255 255 / 24%);
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}
.element-picker-nav {
  display: inline-flex;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: var(--rm-ink);
  transition: background-color 120ms;
}
.element-picker-nav:hover:not(:disabled) {
  background: var(--rm-overlay);
}
.element-picker-nav:disabled {
  color: var(--rm-faint);
  cursor: default;
  opacity: 0.6;
}
.element-picker-address {
  display: flex;
  height: 36px;
  min-width: 0;
  flex: 1;
  align-items: center;
  gap: 8px;
  margin: 0 6px;
  padding: 0 4px 0 14px;
  border: 1px solid var(--rm-line);
  border-radius: 9999px;
  background: var(--p-form-field-background);
}
.element-picker-address:focus-within {
  outline: 2px solid var(--rm-primary);
  outline-offset: 1px;
}
.element-picker-mode :deep(.p-togglebutton) {
  padding: 3px;
  font-size: 13px;
  font-weight: 600;
}
.element-picker-mode :deep(.p-togglebutton-content) {
  gap: 6px;
  padding: 4px 12px;
}
</style>

<style>
.element-picker {
  --element-picker-pink: #db2777;
  --element-picker-tint: color-mix(in srgb, #db2777 12%, transparent);
  --element-picker-ink: #b02a6b;
}
.dark .element-picker {
  --element-picker-ink: #f9a8d4;
}
</style>
